import { CheckCircleTwoTone } from '@ant-design/icons';
import { usePremiumAccess } from '@hellodarwin/app-core/lib/providers';
import HdArrow from '@hellodarwin/core/lib/components/common/button/arrow';
import Div from '@hellodarwin/core/lib/components/common/div';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import Loading from '@hellodarwin/core/lib/components/loading';
import {
  CompanyTourStepResponse,
  isNPO,
} from '@hellodarwin/core/lib/features/entities';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import Col from 'antd/es/col';
import Progress from 'antd/es/progress';
import { Link } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../../app/app-hooks';
import { openCompleteProfileModal } from '../../../features/api/slices/global-slice';
import {
  selectCompany,
  selectTourProgress,
} from '../../../features/api/slices/profile-slice';
import { ChecklistRow } from './style';

interface TourChecklistContentProps {
  tourChecklist: CompanyTourStepResponse[];
  showTitle?: boolean;
  showProgress?: boolean;
  isLoading?: boolean;
}

interface ChecklistItem {
  key: string;
  link?: string;
  action?: () => void;
}

const TourChecklistContent: React.FC<TourChecklistContentProps> = ({
  tourChecklist,
  showTitle = true,
  showProgress = true,
  isLoading = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslations();

  const tourProgress = useAppSelector(selectTourProgress);
  const { accessLevel } = usePremiumAccess();
  const dispatch = useAppDispatch();
  const handleAction = (action: () => void) => {
    action();
  };
  const company = useAppSelector(selectCompany);
  const companyIsNPO = isNPO(company.legal_structure ?? '');

  const checklistItemsFreePlan: ChecklistItem[] = [
    {
      key: 'update_company',
      action: () => {
        dispatch(openCompleteProfileModal({ context: 'funding-explorer' }));
      },
    },
    {
      key: 'funding_search',
      link: '/funding-explorer',
    },
    {
      key: 'grant_viewed',
      link: '/funding-explorer',
    },
    {
      key: 'create_program',
      link: '/funding-explorer',
    },
  ];

  const checklistItemsPremiumPlan: ChecklistItem[] = [
    // {
    //   key: 'book_call',
    //   action: () => {
    //     window.open(
    //       `https://zoom.us/meeting/register/ApH1tOSYQGmu9EJAaLoBvw#/registration`,
    //       '_blank',
    //     );
    //   },
    // },
    {
      key: 'start_application_work',
      link: '/applications',
    },
    {
      key: 'assistant_conversation',
      link: '/assistant',
    },
    {
      key: 'subscribe_program_alerts',
      link: '/alerts',
    },
    {
      key: 'funding_intelligence',
      link: '/funding-intelligence',
    },
  ];

  const checklistItemsExpertPlan: ChecklistItem[] = [
    {
      key: 'create_grant_profile',
      link: '/grant-profiles?create=true',
    },
    {
      key: 'view_funding_explorer',
      link: '/funding-explorer',
    },
  ];

  const checklistItems =
    accessLevel === 'expert'
      ? checklistItemsExpertPlan
      : accessLevel === 'premium'
        ? checklistItemsPremiumPlan
        : checklistItemsFreePlan;

  const checklistItemMap = new Map(
    checklistItems.map((item) => [item.key, item]),
  );

  const getChecklistRowTranslation = (stepName: string) => {
    if (stepName === 'create_grant_profile') {
      return t('grantProfiles|actionsCreate');
    }
    if (stepName === 'view_funding_explorer') {
      return t('navigation|fundingExplorer2');
    }
    // If the company is an NPO, the "update_company" step should use a different translation
    if (stepName === 'update_company' && companyIsNPO) {
      return t(`dashboardChecklist|update_company_npo`);
    }

    return t(`dashboardChecklist|${stepName}`);
  };

  if (isLoading && tourChecklist.length === 0) {
    return <Loading />;
  }

  return (
    <>
      {showTitle && (
        <Typography elementTheme="subtitle1">
          {t('dashboardChecklist|title')}
        </Typography>
      )}
      {showProgress && (
        <Progress
          percent={tourProgress.percentage}
          strokeColor={theme.colors.green_1}
        />
      )}
      <Col>
        {tourChecklist.map(
          (item, index) =>
            item.step_name !== 'book_call' && (
              <ChecklistRow key={index}>
                {item.completed_at ? (
                  <CheckCircleTwoTone
                    twoToneColor={[theme.colors.green_1, theme.colors.green_3]}
                    style={{ fontSize: '24px' }}
                  />
                ) : (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ flexShrink: 0 }}
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke={theme.colors.grey_2}
                      fill={theme.colors.grey_5}
                      strokeWidth="2"
                    />
                  </svg>
                )}

                {(() => {
                  const checklistItem = checklistItemMap.get(item.step_name);
                  const contentStyle = {
                    display: 'inline-flex',
                    alignItems: 'center',
                    width: 'fit-content',
                    gap: theme.spacing.sm,
                    color: item.completed_at
                      ? theme.colors.grey_4
                      : theme.colors.grey_1,
                  };

                  const content = (
                    <>
                      <Typography
                        elementTheme="body3"
                        color={
                          item.completed_at
                            ? theme.colors.grey_4
                            : theme.colors.grey_1
                        }
                        style={{
                          marginLeft: theme.spacing.sm,
                          textDecoration:
                            item.completed_at !== null
                              ? 'line-through'
                              : 'none',
                          display: 'inline',
                        }}
                      >
                        {getChecklistRowTranslation(item.step_name)}
                      </Typography>
                      <Div
                        flex="column"
                        style={{
                          width: 'fit-content',
                          alignItems: 'center',
                          color: item.completed_at
                            ? theme.colors.grey_4
                            : theme.colors.grey_1,
                        }}
                      >
                        {item.completed_at === null && <HdArrow dir="right" />}
                      </Div>
                    </>
                  );

                  if (checklistItem?.action) {
                    return (
                      <Div
                        onClick={() => handleAction(checklistItem.action!)}
                        style={{ ...contentStyle, cursor: 'pointer' }}
                      >
                        {content}
                      </Div>
                    );
                  }

                  return (
                    <Link
                      to={checklistItem?.link ?? ''}
                      style={{ ...contentStyle, textDecoration: 'none' }}
                    >
                      {content}
                    </Link>
                  );
                })()}
              </ChecklistRow>
            ),
        )}
      </Col>
    </>
  );
};

export default TourChecklistContent;
